import { useState, useEffect, useId, ChangeEvent } from "react";
import { Link } from "react-router-dom";
import { Button } from "@mui/material";

import "./style.scss";
import { AuthErrorMessage } from "../../../common/enums/auth-error-message.enum";
import { FirebaseAuthContainer } from "../../../common/containers/auth/firebase-auth-container.util";
import { emailRegex } from "../../../common/utilities/constants/constants";
import { useTimer } from "../../../common/utilities/timer/use-timer.util";
import { AuthTemplate } from "../../../components/auth-template/auth-template.comp";
import { Loader } from "../../../components/loader/loader.comp";

const ForgotPassword = () => {
    const { sendPasswordReset } = FirebaseAuthContainer.useContainer();
    const [login, setLogin] = useState<string>("");
    const [errorMessage, setErrorMessage] = useState<string>("");
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isInvalid, setIsInvalid] = useState<boolean>(false);
    const [resetSent, setResetSent] = useState<boolean>(false);
    const [sendResetnWaitingTime, setSendResetWaitingTime] = useState<number>();
    const { timerStarted, timerTimeLeft, startTimer, stopTimer } = useTimer();
    const errorId = useId();

    useEffect(() => {
        return () => {
            stopTimer();
        };
    }, [stopTimer]);

    useEffect(() => {
        setSendResetWaitingTime(timerStarted ? timerTimeLeft : undefined);
    }, [timerStarted, timerTimeLeft]);

    const handleLoginChange = (evt: ChangeEvent<HTMLInputElement>) => {
        const val = evt.target?.value;
        setLogin(val ?? "");
        setErrorMessage("");
        setIsInvalid(false);
    };

    const handleSendAgain = () => {
        if (!login) return;

        // Pass current tenant URL as returnUrl for password reset redirect
        const currentTenantUrl = window.location.origin;
        sendPasswordReset(login, currentTenantUrl, currentTenantUrl).then(() => {
            startTimer(60);
        });
    };

    const handleResetClick = () => {
        if (!login) return;

        if (!login.match(emailRegex)) {
            setIsInvalid(true);
            setErrorMessage(AuthErrorMessage.invalidEmail);
            return;
        }

        setIsLoading(true);
        // Pass current tenant URL as returnUrl for password reset redirect
        const currentTenantUrl = window.location.origin;
        sendPasswordReset(login, currentTenantUrl, currentTenantUrl)
            .then(() => {
                startTimer(60);
                setResetSent(true);
            })
            .catch(() => {
                setErrorMessage(AuthErrorMessage.default);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    return (
        <AuthTemplate>
            <div className="forgot-password">
                <div className="forgot-password-title" role="heading" aria-level={1}>
                    {!resetSent ? "Forgot Password?" : "Confirm Reset"}
                </div>
                <div className="forgot-password-description">
                    {!resetSent ? (
                        <span className="forgot-password-description__text">
                            Enter your email to reset password.
                            <br></br>
                            You will get verification email to continue.
                        </span>
                    ) : (
                        <>
                            <div className="forgot-password-content">
                                <div className="forgot-password-login">
                                    <span className="forgot-password-login__text">
                                        If you&apos;ve remembered your password, you can
                                    </span>
                                    <Link to="/login" className="forgot-password-login__link">
                                        Log In
                                    </Link>
                                </div>
                            </div>
                            <span className="forgot-password-description__text">
                                <span>Confirmation link was sent to </span>
                                <span className="forgot-password-description__text-email">
                                    {login}
                                </span>
                                .<br></br>
                                Click on the link in the email to reset password.
                            </span>
                        </>
                    )}
                </div>
                {!resetSent ? (
                    <div className="forgot-password-content">
                        <div className="forgot-password-field">
                            <input
                                type="email"
                                aria-label="Email"
                                autoComplete="email"
                                required
                                aria-invalid={errorMessage ? true : undefined}
                                aria-describedby={errorMessage ? errorId : undefined}
                                placeholder="Email"
                                onChange={handleLoginChange}
                                value={login}
                            />
                        </div>
                        <div id={errorId} className="forgot-password-message" role="alert">
                            {isInvalid && <span>{errorMessage}</span>}
                        </div>
                        <div className="forgot-password-login">
                            <span className="forgot-password-login__text">
                                If you&apos;ve remembered your password, you can
                            </span>
                            <Link to="/login" className="forgot-password-login__link">
                                Log In
                            </Link>
                        </div>
                    </div>
                ) : (
                    <div className="forgot-password-content">
                        <span className="forgot-password-send-again">
                            Didn&apos;t receive it? Check spam or send again<br></br>
                            {sendResetnWaitingTime ? `(in ${sendResetnWaitingTime}s)` : ""}
                        </span>
                    </div>
                )}
                <div className="forgot-password-action">
                    {!resetSent ? (
                        <Button
                            className="primary-button"
                            variant="text"
                            onClick={handleResetClick}
                            disabled={!login || isLoading || isInvalid}
                        >
                            {!isLoading ? <span>Send</span> : <Loader size={24} borderWidth={4} />}
                        </Button>
                    ) : (
                        <Button
                            className="primary-button"
                            variant="text"
                            onClick={handleSendAgain}
                            disabled={!!sendResetnWaitingTime}
                        >
                            Send Again
                        </Button>
                    )}
                </div>
            </div>
        </AuthTemplate>
    );
};

export default ForgotPassword;
